import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { BottomNavComponent } from '../bottom-nav/bottom-nav.component';
import { TenantService } from '../../services/tenant.service';
import { LanguageService } from '../../services/language.service';
import { AppContext, ContextService } from '../../services/context.service';
import { NavigationHistoryService } from '../../core/navigation/navigation-history.service';
import { MobileNavItem, buildMobileNavItems } from './mobile-nav.config';

@Component({
  selector: 'app-mobile-shell',
  standalone: true,
  imports: [CommonModule, RouterModule, TranslateModule, BottomNavComponent],
  templateUrl: './mobile-shell.component.html',
  styleUrl: './mobile-shell.component.scss'
})
export class MobileShellComponent implements OnInit, OnDestroy {
  tenantLabel: string | null = null;
  navItems: MobileNavItem[] = [];
  supportedLanguages = this.languageService.getSupportedLanguages();
  currentLanguage$ = this.languageService.currentLanguage$;
  showBack = false;

  private currentContext: AppContext = 'admin';
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly tenantService: TenantService,
    private readonly contextService: ContextService,
    private readonly languageService: LanguageService,
    private readonly navigationHistory: NavigationHistoryService,
    private readonly location: Location,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.currentContext = this.contextService.getCurrentContext();
    this.navItems = buildMobileNavItems(this.currentContext);

    const tenant = this.tenantService.getCurrentTenantSync();
    this.tenantLabel = tenant?.name || tenant?.schema_name || null;

    this.contextService.context$
      .pipe(takeUntil(this.destroy$))
      .subscribe((context) => {
        this.currentContext = context;
        this.navItems = buildMobileNavItems(context);
      });

    this.updateBackState(this.router.url);
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event) => {
        this.updateBackState(event.urlAfterRedirects);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setLanguage(language: 'pl' | 'en' | 'de'): void {
    this.languageService.setLanguage(language);
  }

  onBack(): void {
    this.navigationHistory.backOrFallback(this.location, this.resolveFallbackRoute());
  }

  private updateBackState(url: string): void {
    const cleanUrl = url.split('?')[0].split('#')[0];
    this.showBack = cleanUrl !== '/dashboard';
  }

  private resolveFallbackRoute(): string {
    const url = this.router.url.split('?')[0].split('#')[0];
    if (url === '/profile') {
      return '/dashboard';
    }

    const contextItem = this.navItems.find((item) => item.id === 'context');
    return contextItem?.route ?? '/dashboard';
  }
}
